import { mulQ4, toSafe } from "./arith";
import { allocateProportional } from "./commission";
import { DomainError } from "./errors";
import { NFSE_MAX_DESCRIPTION } from "./nfse";
import { formatQty } from "./qty";
import { MAX_DOCUMENT_CENTS } from "./quotes";
import { MAX_STOCK_QTY_Q4 } from "./stock";
import type { BaseState, EffectState } from "./status-flow";

// ===== Ordem de serviço (T2.9, RF-SER-03) =====

/** Itens (serviços + peças) por OS: a tela e o PDF mostram todos. */
export const MAX_SERVICE_ORDER_ITEMS = 200;
export const SERVICE_ORDER_ITEM_KINDS = ["service", "part"] as const;
export type ServiceOrderItemKind = (typeof SERVICE_ORDER_ITEM_KINDS)[number];
/** Situação de sistema em que "Finalizar" leva a OS (baixa as peças e lança as contas). */
export const SERVICE_ORDER_FINISHED = "finalizada";
/** Situação concluída que não emite notas (nada foi consertado nem cobrado). */
export const SERVICE_ORDER_UNREPAIRED = "finalizada_sem_conserto";

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

const money = (v: number, what: string) => {
  if (!Number.isSafeInteger(v) || v < 0 || v > MAX_DOCUMENT_CENTS)
    fail("invalid_money", `${what} inválido: ${v}`);
};

export type ServiceOrderLineInput = { kind: ServiceOrderItemKind; qtyQ4: number; unitPriceCents: number };
export type ServiceOrderTotals = {
  lines: { totalCents: number }[];
  servicesTotalCents: number;
  partsTotalCents: number;
  freightCents: number;
  discountCents: number;
  totalCents: number;
  /** Soma das horas dos serviços (q4). */
  hoursSumQ4: number;
};

/**
 * Totais da OS (05 §3): serviço = horas × preço/hora, peça = quantidade × preço (q4 × centavos, meio para cima); total
 * = serviços + peças + frete − desconto. Desconto até a soma dos itens (`invalid_discount`). Somas em BigInt; acima
 * de MAX_DOCUMENT_CENTS = `total_limit`.
 */
export function serviceOrderTotals(i: {
  items: readonly ServiceOrderLineInput[];
  freightCents: number;
  discountCents: number;
}): ServiceOrderTotals {
  if (i.items.length > MAX_SERVICE_ORDER_ITEMS)
    fail("too_many_items", `No máximo ${MAX_SERVICE_ORDER_ITEMS} itens por ordem de serviço`);
  money(i.freightCents, "Frete");
  money(i.discountCents, "Desconto");
  let services = 0n;
  let parts = 0n;
  let hours = 0n;
  const lines = i.items.map((it) => {
    if (!Number.isSafeInteger(it.qtyQ4) || it.qtyQ4 <= 0 || it.qtyQ4 > MAX_STOCK_QTY_Q4)
      fail("invalid_qty", `Quantidade inválida: ${it.qtyQ4}`);
    money(it.unitPriceCents, "Preço");
    const totalCents = mulQ4(it.unitPriceCents, it.qtyQ4);
    if (it.kind === "service") {
      services += BigInt(totalCents);
      hours += BigInt(it.qtyQ4);
    } else parts += BigInt(totalCents);
    return { totalCents };
  });
  const items = services + parts;
  if (BigInt(i.discountCents) > items)
    fail("invalid_discount", "O desconto não pode passar da soma dos serviços e das peças");
  const total = items + BigInt(i.freightCents) - BigInt(i.discountCents);
  if (items > BigInt(MAX_DOCUMENT_CENTS) || total > BigInt(MAX_DOCUMENT_CENTS))
    fail("total_limit", "Total da ordem de serviço acima do limite");
  return {
    lines,
    servicesTotalCents: toSafe(services, "total_limit"),
    partsTotalCents: toSafe(parts, "total_limit"),
    freightCents: i.freightCents,
    discountCents: i.discountCents,
    totalCents: toSafe(total, "total_limit"),
    hoursSumQ4: toSafe(hours, "invalid_qty"),
  };
}

export type ServiceOrderFiscalSplit = {
  /** NFS-e dos serviços (null = OS sem serviços). */
  nfse: { servicesCents: number; discountCents: number } | null;
  /** NF-e das peças (null = OS sem peças). */
  nfe: { itemsCents: number; freightCents: number; discountCents: number } | null;
};

/**
 * Valores das notas da OS: serviços na NFS-e, peças e frete na NF-e; o desconto da OS é rateado entre serviços e
 * peças pelo valor (centavo do rateio na parte maior, BigInt). Sem peças, o frete vai para a NFS-e.
 */
export function serviceOrderFiscalSplit(i: {
  servicesTotalCents: number;
  partsTotalCents: number;
  freightCents: number;
  discountCents: number;
}): ServiceOrderFiscalSplit {
  const [svcDiscount, partDiscount] = allocateProportional(i.discountCents, [
    i.servicesTotalCents,
    i.partsTotalCents,
  ]) as [number, number];
  const hasParts = i.partsTotalCents > 0;
  return {
    nfse:
      i.servicesTotalCents > 0
        ? {
            servicesCents: i.servicesTotalCents + (hasParts ? 0 : i.freightCents),
            discountCents: svcDiscount,
          }
        : null,
    nfe: hasParts
      ? { itemsCents: i.partsTotalCents, freightCents: i.freightCents, discountCents: partDiscount }
      : null,
  };
}

export type PaymentPart = { dueDate: string; amountCents: number; paymentMethodId: string | null };

/** Parcelas da OS rateadas pelo valor de uma das notas (soma exata; parcelas zeradas saem). */
export function splitPayments(parts: readonly PaymentPart[], targetCents: number): PaymentPart[] {
  if (parts.length === 0) return [];
  const amounts = allocateProportional(
    targetCents,
    parts.map((p) => p.amountCents),
  );
  return parts.map((p, k) => ({ ...p, amountCents: amounts[k]! })).filter((p) => p.amountCents > 0);
}

/** Trava de itens e valores da OS (`order_locked`), ou null. */
export function serviceOrderLockProblem(effects: EffectState, base: BaseState): string | null {
  if (effects.stock === "reserved")
    return "A ordem de serviço tem peças reservadas. Libere a reserva antes de alterar.";
  if (effects.stock === "posted")
    return "A ordem de serviço tem peças baixadas do estoque. Volte a situação para estornar antes de alterar.";
  if (effects.financial === "posted")
    return "A ordem de serviço tem contas lançadas. Volte a situação para estornar antes de alterar.";
  if (base === "done") return "Ordem de serviço finalizada não pode ser alterada.";
  if (base === "canceled") return "Ordem de serviço não aprovada não pode ser alterada.";
  return null;
}

/** Só a OS finalizada (estado base concluído, exceto "Finalizada sem conserto") emite NFS-e e NF-e. */
export const isServiceOrderInvoiceable = (base: BaseState, statusCode: string): boolean =>
  base === "done" && statusCode !== SERVICE_ORDER_UNREPAIRED;

/** Descrição do serviço na NFS-e: "Ordem de serviço nº N: Troca de tela (1,5 h); …", cortada no limite da DPS. */
export function serviceOrderNfseDescription(
  number: number,
  services: readonly { description: string; qtyQ4: number }[],
): string {
  const list = services
    .map((s) => `${s.description.trim().replace(/\s+/g, " ")} (${formatQty(s.qtyQ4)} h)`)
    .join("; ");
  const text = list ? `Ordem de serviço nº ${number}: ${list}` : `Ordem de serviço nº ${number}`;
  return text.length > NFSE_MAX_DESCRIPTION ? `${text.slice(0, NFSE_MAX_DESCRIPTION - 1)}…` : text;
}
